import type { Aircraft } from '../types';

/**
 * Observed flight-lane density: an in-memory spatial grid of where aircraft
 * have actually been flying recently, derived purely from live positions
 * this app already receives. This is NOT official airway/route chart data —
 * there is no free bundled dataset of Victor airways / RNAV routes / FAA
 * NASR data anywhere in this app — it's an empirical "where has traffic
 * really been" heatmap that decays over time so it reflects recent
 * conditions rather than accumulating forever.
 */

/**
 * Grid cell size in degrees. ~0.06° is roughly 6-7 km at mid-latitudes —
 * coarse enough that individual noisy fixes blur into smooth "lanes" at the
 * zoom levels this app is normally viewed at, fine enough that distinct
 * corridors (e.g. parallel approach paths) don't smear into one blob.
 */
const CELL_DEG = 0.06;

/**
 * Hard expiry: observations older than this are dropped outright, regardless
 * of decayed weight, so a cell that saw one flight an hour ago and nothing
 * since doesn't linger indefinitely. 45 min balances "long enough to build a
 * readable pattern from sparse traffic" against "recent enough to still be
 * true now."
 */
const WINDOW_MS = 45 * 60 * 1000;

/**
 * Exponential half-life used to weight recency within the window — traffic
 * from 15 minutes ago counts for about half as much as traffic right now.
 * Combined with the hard window above, this makes the busiest, freshest
 * corridors stand out rather than every cell that was ever touched reading
 * as equally "hot".
 */
const HALF_LIFE_MS = 15 * 60 * 1000;
const DECAY_PER_MS = Math.log(0.5) / HALF_LIFE_MS;

/** Minimum decayed weight below which a cell is treated as noise and dropped. */
const MIN_WEIGHT = 0.05;

interface DensityCell {
  /** Cell-center latitude/longitude, used directly as the rendered point. */
  lat: number;
  lon: number;
  /** Decayed observation weight as of `lastTs`. */
  weight: number;
  lastTs: number;
}

export type DensityGrid = Map<string, DensityCell>;

function cellKey(cy: number, cx: number): string {
  return `${cy}:${cx}`;
}

export function createDensityGrid(): DensityGrid {
  return new Map();
}

/**
 * Feed current airborne aircraft positions into the grid. Call this every
 * time the live aircraft set changes. Ground traffic is excluded — "flight
 * lanes" means airborne corridors, not taxiways/ramps.
 */
export function accumulate(grid: DensityGrid, aircraft: Iterable<Aircraft>, now = Date.now()): void {
  for (const ac of aircraft) {
    if (ac.onGround) continue;
    if (!Number.isFinite(ac.lat) || !Number.isFinite(ac.lon)) continue;

    const cy = Math.round(ac.lat / CELL_DEG);
    const cx = Math.round(ac.lon / CELL_DEG);
    const key = cellKey(cy, cx);

    let cell = grid.get(key);
    if (!cell) {
      cell = { lat: cy * CELL_DEG, lon: cx * CELL_DEG, weight: 0, lastTs: now };
      grid.set(key, cell);
    }
    const elapsed = now - cell.lastTs;
    if (elapsed > 0) cell.weight *= Math.exp(DECAY_PER_MS * elapsed);
    cell.weight += 1;
    cell.lastTs = now;
  }
}

/**
 * Drop cells that have fully aged out of the rolling window. Call
 * periodically (e.g. alongside a redraw) to keep the grid's memory bounded —
 * otherwise every cell ever touched would stay in the map forever, just with
 * a near-zero weight.
 */
export function prune(grid: DensityGrid, now = Date.now()): void {
  for (const [key, cell] of grid) {
    if (now - cell.lastTs > WINDOW_MS) grid.delete(key);
  }
}

/**
 * Snapshot the grid as a GeoJSON point FeatureCollection weighted by current
 * (decayed-to-now) density, suitable for a MapLibre `heatmap` layer's
 * `heatmap-weight` data expression.
 */
export function toFeatureCollection(grid: DensityGrid, now = Date.now()): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const cell of grid.values()) {
    const elapsed = now - cell.lastTs;
    if (elapsed > WINDOW_MS) continue;
    const weight = cell.weight * Math.exp(DECAY_PER_MS * Math.max(elapsed, 0));
    if (weight < MIN_WEIGHT) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [cell.lon, cell.lat] },
      properties: { weight },
    });
  }
  return { type: 'FeatureCollection', features };
}
